import { zodResolver } from "@hookform/resolvers/zod";
import { Plug, Save, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTestConnection } from "@/hooks/use-bu-instances";
import { type BuInstanceFormValues, buInstanceFormSchema } from "@/schemas/bu-instances";

// Not a persisted enum column (DB stores a plain boolean), so this stays a
// local UI mapping rather than a src/lib/enums.ts entry.
const ACTIVE_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

interface BuInstanceFormResult {
  name: string;
  endpointUrl: string;
  username: string;
  /** undefined means "keep the existing credential" (edit only). */
  password?: string;
  companyId?: string;
  active: boolean;
}

interface BuInstanceFormDefaults {
  name?: string;
  endpointUrl?: string;
  username?: string;
  companyId?: string;
  active?: boolean;
}

interface BuInstanceFormProps {
  defaultValues?: BuInstanceFormDefaults;
  /** Shows the "leave blank to keep existing" hint and makes password optional. */
  isEdit?: boolean;
  onSubmit: (result: BuInstanceFormResult) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitLabel?: string;
}

/**
 * One reusable form drives both create and edit — mirrors
 * `src/examples/widget/WidgetForm.tsx`. The password field always starts
 * blank; no endpoint ever returns a stored credential to prefill it (see the
 * Fleet Relay build plan's Phase 2 credential-handling decision), so "Test
 * Connection" only works once a password has been (re-)typed in this form.
 */
export function BuInstanceForm({
  defaultValues,
  isEdit = false,
  onSubmit,
  onCancel,
  isSaving,
  submitLabel = "Save",
}: BuInstanceFormProps) {
  // Password is required on create but optional on edit (blank = keep the
  // existing credential) — the same base schema serves both by extending it
  // only when this isn't an edit.
  const schema = useMemo(
    () =>
      isEdit
        ? buInstanceFormSchema
        : buInstanceFormSchema.extend({ password: z.string().min(1, "Password is required") }),
    [isEdit],
  );

  const form = useForm<BuInstanceFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaultValues?.name ?? "",
      endpointUrl: defaultValues?.endpointUrl ?? "",
      username: defaultValues?.username ?? "",
      password: "",
      companyId: defaultValues?.companyId ?? "",
      active: defaultValues?.active ?? true,
    },
  });

  const testConnection = useTestConnection();
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [endpointUrl, username, password] = form.watch(["endpointUrl", "username", "password"]);
  const canTest = Boolean(endpointUrl && username && password);

  function handleTestConnection() {
    setTestResult(null);
    testConnection.mutate(
      { endpointUrl, username, password: password ?? "" },
      {
        onSuccess: (result) => {
          setTestResult(
            result.ok
              ? { ok: true, message: result.resultDescription || "Connected successfully." }
              : { ok: false, message: result.error },
          );
        },
        onError: (err) => {
          setTestResult({
            ok: false,
            message: err instanceof Error ? err.message : "Test connection failed.",
          });
        },
      },
    );
  }

  function handleSubmit(values: BuInstanceFormValues) {
    onSubmit({
      name: values.name,
      endpointUrl: values.endpointUrl,
      username: values.username,
      password: values.password?.trim() ? values.password : undefined,
      companyId: values.companyId?.trim() ? values.companyId : undefined,
      active: values.active,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Acme Coach Co" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="endpointUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Endpoint URL</FormLabel>
              <FormControl>
                <Input placeholder="https://soap.example.com/Sandbox/cm.asmx" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="service-account" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    placeholder={isEdit ? "Leave blank to keep existing" : undefined}
                    {...field}
                  />
                </FormControl>
                {isEdit && (
                  <FormDescription>Leave blank to keep the current credential.</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="companyId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company ID</FormLabel>
              <FormControl>
                <Input placeholder="e.g. 1" {...field} />
              </FormControl>
              <FormDescription>
                Coach Manager's fixed CompanyID for this BU — only required when this BU is used as
                a copy target.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <FormControl>
                <EnumSelect
                  value={field.value ? "true" : "false"}
                  onValueChange={(value) => field.onChange(value === "true")}
                  options={ACTIVE_OPTIONS}
                  className="w-[180px]"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Test Connection</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!canTest || testConnection.isPending}
              onClick={handleTestConnection}
            >
              <Plug className="h-4 w-4 mr-1" />
              {testConnection.isPending ? "Testing…" : "Test Connection"}
            </Button>
          </div>
          {!canTest && (
            <p className="text-xs text-muted-foreground">
              Enter the endpoint URL, username, and password to test.
            </p>
          )}
          {testResult && (
            <p
              className={
                testResult.ok
                  ? "text-xs text-emerald-600 dark:text-emerald-400"
                  : "text-xs text-destructive"
              }
            >
              {testResult.message}
            </p>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isSaving}>
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? "Saving…" : submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
}
